import type {
  FeatureDto,
  ProjectDto,
  ReleaseDetailDto,
  SuiteDetailDto,
  TestDetailDto,
} from '@flowqa/shared';

// DTO-to-form adapters for core entities shared across feature pages.
export type NameValues = { name: string };

export type FeatureFormValues = NameValues & {
  projectId: string;
  status: FeatureDto['status'];
  releaseId: string;
  plannedFor: string;
  releasedAt: string;
};

export type ReleaseFormValues = NameValues & {
  projectId: string;
  status: ReleaseDetailDto['status'];
  plannedFor: string;
  releasedAt: string;
};

export type SuiteFormValues = NameValues & { projectId: string };

export type TestFormValues = NameValues & {
  projectId: string;
  suiteIds: string[];
};

type ProjectPayload = { name: string };

type FeatureCreatePayload = {
  name: string;
  status: FeatureDto['status'];
  projectId: string;
  releaseId?: string;
  plannedFor: string | null;
  releasedAt: string | null;
};

type FeatureUpdatePayload = {
  name: string;
  status: FeatureDto['status'];
  releaseId: string | null;
  plannedFor: string | null;
  releasedAt: string | null;
};

type ReleaseCreatePayload = {
  name: string;
  status: ReleaseDetailDto['status'];
  projectId: string;
  plannedFor: string | null;
  releasedAt: string | null;
};

type ReleaseUpdatePayload = {
  name: string;
  status: ReleaseDetailDto['status'];
  plannedFor: string | null;
  releasedAt: string | null;
};

type SuiteCreatePayload = {
  name: string;
  projectId: string;
};

type SuiteUpdatePayload = { name: string };

type TestCreatePayload = {
  name: string;
  projectId: string;
  suiteIds?: string[];
};

type TestUpdatePayload = {
  name: string;
  suiteIds: string[];
};

const normaliseText = (value: string): string => value.trim();

const normaliseOptionalId = (value: string | null | undefined): string | undefined => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed.length > 0 ? trimmed : undefined;
};

const normaliseOptionalIdToNull = (value: string | null | undefined): string | null => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed.length > 0 ? trimmed : null;
};

const normaliseDateInput = (value: string | null | undefined): string | null => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed.length > 0 ? trimmed : null;
};

const formatDateInput = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const mapName = (values: NameValues): ProjectPayload => ({
  name: normaliseText(values.name),
});

export const entityFormAdapters = {
  project: {
    toPayload: (values: NameValues): ProjectPayload => mapName(values),
    toFormValues: (project?: ProjectDto | null): ProjectPayload => ({
      name: project?.name ?? '',
    }),
  },
  feature: {
    toCreatePayload: (values: FeatureFormValues, projectId: string): FeatureCreatePayload => ({
      ...mapName(values),
      status: values.status as FeatureDto['status'],
      projectId,
      releaseId: normaliseOptionalId(values.releaseId),
      plannedFor: normaliseDateInput(values.plannedFor),
      releasedAt: normaliseDateInput(values.releasedAt),
    }),
    toUpdatePayload: (values: FeatureFormValues): FeatureUpdatePayload => ({
      ...mapName(values),
      status: values.status as FeatureDto['status'],
      releaseId: normaliseOptionalIdToNull(values.releaseId),
      plannedFor: normaliseDateInput(values.plannedFor),
      releasedAt: normaliseDateInput(values.releasedAt),
    }),
    toFormValues: (feature?: FeatureDto | null): FeatureFormValues => ({
      name: feature?.name ?? '',
      projectId: feature?.projectId ?? '',
      status: feature?.status ?? 'draft',
      releaseId: feature?.releaseId ?? '',
      plannedFor: formatDateInput(feature?.plannedFor),
      releasedAt: formatDateInput(feature?.releasedAt),
    }),
  },
  release: {
    toCreatePayload: (values: ReleaseFormValues, projectId: string): ReleaseCreatePayload => ({
      ...mapName(values),
      status: values.status as ReleaseDetailDto['status'],
      projectId,
      plannedFor: normaliseDateInput(values.plannedFor),
      releasedAt: normaliseDateInput(values.releasedAt),
    }),
    toUpdatePayload: (values: ReleaseFormValues): ReleaseUpdatePayload => ({
      ...mapName(values),
      status: values.status as ReleaseDetailDto['status'],
      plannedFor: normaliseDateInput(values.plannedFor),
      releasedAt: normaliseDateInput(values.releasedAt),
    }),
    toFormValues: (release?: ReleaseDetailDto | null): ReleaseFormValues => ({
      name: release?.name ?? '',
      projectId: release?.projectId ?? '',
      status: release?.status ?? 'draft',
      plannedFor: formatDateInput(release?.plannedFor),
      releasedAt: formatDateInput(release?.releasedAt),
    }),
  },
  suite: {
    toCreatePayload: (values: SuiteFormValues, projectId: string): SuiteCreatePayload => ({
      ...mapName(values),
      projectId,
    }),
    toUpdatePayload: (values: SuiteFormValues): SuiteUpdatePayload => ({
      ...mapName(values),
    }),
    toFormValues: (suite?: SuiteDetailDto | null): SuiteFormValues => ({
      name: suite?.name ?? '',
      projectId: suite?.projectId ?? '',
    }),
  },
  test: {
    toCreatePayload: (values: TestFormValues, projectId: string): TestCreatePayload => ({
      ...mapName(values),
      projectId,
      suiteIds: values.suiteIds.length > 0 ? values.suiteIds : undefined,
    }),
    toUpdatePayload: (values: TestFormValues): TestUpdatePayload => ({
      ...mapName(values),
      suiteIds: values.suiteIds,
    }),
    toFormValues: (test?: TestDetailDto | null): TestFormValues => ({
      name: test?.name ?? '',
      projectId: test?.projectId ?? '',
      suiteIds: Array.isArray(test?.suites?.ids) ? test.suites.ids : [],
    }),
  },
};
